import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography,
} from '@mui/material';
import {
  Download as DownloadIcon, Edit as EditIcon, History as HistoryIcon, PictureAsPdf as PdfIcon, WarningAmber as WarnIcon,
} from '@mui/icons-material';
import { projetDessinsAPI } from '../../services/api';
import { ProjetDessin } from '../../types/projetDessin';
import { DxfPreview, dxfPreview, isBinaryDxf } from '../../utils/dxfPreview';
import { PlanKindChip, isDxfName, isImageFile, isPdfFile, planMeta } from './planUi';

// Lazy-loaded so the PDF viewer libs stay out of the main bundle.
const PdfViewerDialog = React.lazy(() => import('../PdfViewerDialog'));

const fD = (d?: string | null) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fSize = (b?: number | null) => (b ? (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} Ko` : `${(b / 1048576).toFixed(1)} Mo`) : '');

type Apercu =
  | { kind: 'loading' }
  | { kind: 'image'; url: string }
  | { kind: 'dxf'; g: DxfPreview }
  | { kind: 'message'; text: string; warn?: boolean }
  | { kind: 'pdf' };

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" component="div">{children}</Typography>
  </Grid>
);

/** The plan viewer: image, PDF, DXF drawn on screen, with the plan's details and its versions.
 *  Files are read through the URL the API returned (operators get their own screen's proxy). */
const PlanViewerDialog: React.FC<{ plan: ProjetDessin | null; onClose: () => void; onEdit?: (p: ProjetDessin) => void }> = ({ plan, onClose, onEdit }) => {
  const [vid, setVid] = useState<number | null>(null);
  const [apercu, setApercu] = useState<Apercu>({ kind: 'loading' });
  const [pdf, setPdf] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVid(plan?.latest_version?.id ?? null); }, [plan]);
  const v = plan?.versions.find((x) => x.id === vid) || plan?.latest_version || null;
  // one plan, several files: the file shown (its main file by default)
  const [fi, setFi] = useState(0);
  useEffect(() => { setFi(0); }, [vid, plan]);
  const files = v?.fichiers?.length ? v.fichiers : v ? [{ id: null, file_url: v.file_url, file_name: v.file_name, file_type: v.file_type, file_size: v.file_size }] : [];
  const cur = files[Math.min(fi, files.length - 1)] || null;

  useEffect(() => {
    if (!cur) return undefined;
    let url: string | null = null;
    let live = true;
    const load = async () => {
      if (isPdfFile(cur.file_type, cur.file_name)) { setApercu({ kind: 'pdf' }); return; }
      if (!isImageFile(cur.file_type, cur.file_name) && !isDxfName(cur.file_name)) {
        setApercu({ kind: 'message', text: "Pas d'aperçu pour ce type de fichier : téléchargez-le pour l'ouvrir." });
        return;
      }
      setApercu({ kind: 'loading' });
      try {
        const blob = await projetDessinsAPI.getFileBlob(cur.file_url);
        if (!live) return;
        if (isImageFile(cur.file_type, cur.file_name)) {
          url = URL.createObjectURL(blob);
          setApercu({ kind: 'image', url });
          return;
        }
        const buf = new Uint8Array(await blob.arrayBuffer());
        if (isBinaryDxf(buf)) {
          setApercu({ kind: 'message', warn: true, text: 'Aperçu impossible : ce DXF est enregistré en binaire. Enregistrez-le en « DXF ASCII » depuis le logiciel de dessin, ou téléchargez-le.' });
          return;
        }
        const g = dxfPreview(new TextDecoder('utf-8').decode(buf));
        setApercu(g ? { kind: 'dxf', g } : { kind: 'message', text: "Aucune géométrie lisible dans ce DXF (blocs, textes…) : téléchargez-le pour l'ouvrir dans votre logiciel." });
      } catch {
        if (live) setApercu({ kind: 'message', text: 'Aperçu indisponible. Le fichier reste téléchargeable.' });
      }
    };
    load();
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [cur?.file_url]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!plan) return null;
  const download = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      const blob = await projetDessinsAPI.getFileBlob(cur.file_url);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = cur.file_name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } finally { setBusy(false); }
  };
  const recus = plan.destinataires || [];
  const isLatest = v?.id === plan.latest_version?.id;
  return (
    <>
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <PlanKindChip kind={plan.kind} />
          <Box component="span" sx={{ fontWeight: 700 }}>{plan.title}</Box>
          {plan.piece_ref && <Chip label={plan.piece_ref} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />}
          <Typography component="span" color="text.secondary">· v{v?.version || 1}</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {!isLatest && <Alert severity="info" icon={<HistoryIcon />} sx={{ mb: 2 }}>Ancienne version : une version plus récente existe.</Alert>}
          {files.length > 1 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {files.map((x, i) => (
                <Chip key={`${x.id}-${i}`} label={x.file_name} onClick={() => setFi(i)}
                  color={i === fi ? 'primary' : 'default'} variant={i === fi ? 'filled' : 'outlined'} />
              ))}
            </Stack>
          )}
          <Box sx={{ minHeight: 320, maxHeight: '55vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'action.hover', borderRadius: 2, overflow: 'hidden', mb: 2, p: 1 }}>
            {apercu.kind === 'loading' && <CircularProgress size={28} />}
            {apercu.kind === 'image' && <Box component="img" src={apercu.url} alt={plan.title} sx={{ maxWidth: '100%', maxHeight: '53vh', objectFit: 'contain' }} />}
            {apercu.kind === 'dxf' && (
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Box component="svg" viewBox={apercu.g.viewBox} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Aperçu du plan DXF"
                  sx={{ width: '100%', height: '48vh', color: 'text.primary', bgcolor: 'background.paper', borderRadius: 1 }}>
                  <g transform="scale(1,-1)" fill="none" stroke="currentColor">
                    {apercu.g.paths.map((d, i) => <path key={i} d={d} vectorEffect="non-scaling-stroke" strokeWidth={1.2} />)}
                  </g>
                </Box>
                <Typography variant="caption" color="text.secondary">{apercu.g.dim}</Typography>
              </Box>
            )}
            {apercu.kind === 'message' && (
              <Stack alignItems="center" spacing={1} sx={{ maxWidth: 480, textAlign: 'center' }}>
                {apercu.warn && <WarnIcon color="warning" />}
                <Typography variant="body2" color="text.secondary">{apercu.text}</Typography>
              </Stack>
            )}
            {apercu.kind === 'pdf' && (
              <Button variant="outlined" startIcon={<PdfIcon />} onClick={() => setPdf(true)}>Ouvrir l'aperçu du PDF</Button>
            )}
          </Box>
          <Grid container spacing={1.5}>
            {plan.owner && <Fact label="Projet / service"><b>{plan.owner.code}</b> · {plan.owner.nom}{plan.owner.client ? ` · ${plan.owner.client}` : ''}</Fact>}
            <Fact label="Type">{plan.kind_label}</Fact>
            <Fact label="Travail">{plan.travail_label || 'Tout le projet'}</Fact>
            <Fact label="Machine">{plan.machine ? `${plan.machine.code} — ${plan.machine.name}` : '—'}</Fact>
            <Fact label="Étape">{plan.etape_label || 'Toutes (selon la machine)'}</Fact>
            <Fact label="Fabrication">{planMeta(plan) || '—'}</Fact>
            {plan.destinataires && (
              <Fact label="Reçu par">
                {recus.length ? recus.map((d) => (
                  <Box key={`${d.type}-${d.id}`}>{d.label}{d.machine ? ` · ${d.machine}` : ''} · {d.responsable || 'à désigner'}</Box>
                )) : '—'}
              </Fact>
            )}
            <Fact label={files.length > 1 ? `Fichiers (${files.length})` : 'Fichier'}>{cur ? `${cur.file_name}${cur.file_size ? ` · ${fSize(cur.file_size)}` : ''}` : '—'}</Fact>
            <Fact label="Version">{v ? `v${v.version} · ${fD(v.uploaded_at)}${v.uploaded_by_name ? ` · ${v.uploaded_by_name}` : ''}` : '—'}</Fact>
          </Grid>
          {plan.observations && <Alert severity="warning" sx={{ mt: 2 }}><b>Observations :</b> {plan.observations}</Alert>}
          {v?.note && v.version > 1 && <Alert severity="info" icon={<HistoryIcon />} sx={{ mt: 1 }}>Changement de cette version : {v.note}</Alert>}
          {plan.versions.length > 1 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
              {plan.versions.map((x) => (
                <Chip key={x.id} label={`v${x.version} · ${fD(x.uploaded_at)}`} title={x.note || ''} onClick={() => setVid(x.id)}
                  color={x.id === v?.id ? 'primary' : 'default'} variant={x.id === v?.id ? 'filled' : 'outlined'} />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fermer</Button>
          <Button variant={isDxfName(cur?.file_name) ? 'contained' : 'outlined'} startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={download} disabled={!cur || busy}>
            Télécharger{isDxfName(cur?.file_name) ? ' le DXF' : files.length > 1 ? ' ce fichier' : ''}
          </Button>
          {onEdit && <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(plan)}>Modifier / nouvelle version</Button>}
        </DialogActions>
      </Dialog>
      {pdf && cur && (
        <React.Suspense fallback={null}>
          <PdfViewerDialog open getBlob={() => projetDessinsAPI.getFileBlob(cur.file_url)} fileName={cur.file_name} onClose={() => setPdf(false)} />
        </React.Suspense>
      )}
    </>
  );
};

export default PlanViewerDialog;
